import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { changePassword } from "../../auth/api";
import { loadDepthModels, selectDepthModel, type DepthModel } from "../api";
import Button from "../../../shared/components/Button";
import { Box } from "../../../shared/components/Box";
import { Grid } from "../../../shared/components/Grid";
import { Stack } from "../../../shared/components/Stack";
import { Layout } from "../../../layouts/Layout";
import { Checkbox, TextField } from "../../../shared/components/form";
import type { User } from "../../auth/api";
import "../pages/SettingsPage.scss";

export type SettingsSection = "settings" | "security" | "activity";

export function SettingsDesktopView({
  section = "settings",
  user,
}: {
  section?: SettingsSection;
  user: User;
}) {
  const [desktopAlerts, setDesktopAlerts] = useState(true);
  const [compactCards, setCompactCards] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [depthModels, setDepthModels] = useState<DepthModel[]>([]);
  const [selectedDepthModel, setSelectedDepthModel] = useState("");
  const [modelError, setModelError] = useState("");

  useEffect(() => {
    if (user.role === "viewer") return;
    let active = true;
    loadDepthModels()
      .then((catalog) => {
        if (!active) return;
        setDepthModels(catalog.models);
        setSelectedDepthModel(catalog.selected_model_id);
      })
      .catch(() => {
        if (active) setModelError("Model catalog unavailable.");
      });
    return () => { active = false; };
  }, [user.role]);

  const updateDepthModel = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextModelID = event.target.value;
    const previousModelID = selectedDepthModel;
    setSelectedDepthModel(nextModelID);
    setModelError("");
    void selectDepthModel(nextModelID).catch(() => {
      setSelectedDepthModel(previousModelID);
      setModelError("Model selection rejected.");
    });
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordMessage("");
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }
    setSavingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordMessage("Password updated.");
    } catch (error) {
      setPasswordError(
        error instanceof Error
          ? error.message
          : "Password could not be changed.",
      );
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Layout activePath="/settings" className="settings-page">
      <Box as="main" className="main">
        <header className="header">
          <div>
            <a className="back-link" href="/">
              ← Back to workspace
            </a>
            <p className="eyebrow">{user.role} profile</p>
            <h1>
              {section === "security"
                ? "Security"
                : section === "activity"
                  ? "Activity"
                  : "Settings"}
            </h1>
          </div>
        </header>
        <Grid className="content settings-layout">
          <nav aria-label="Settings navigation" className="settings-nav">
            <p>{user.username}</p>
            <a
              className={section === "settings" ? "active" : ""}
              href="/settings"
            >
              Settings
            </a>
            <a
              className={section === "security" ? "active" : ""}
              href="/settings/security"
            >
              Security
            </a>
            <a
              className={section === "activity" ? "active" : ""}
              href="/settings/activity"
            >
              Activity
            </a>
          </nav>
          <Stack className="settings-content">
            {section === "settings" ? (
              <>
                <section className="settings-section">
                  <div className="section-heading">
                    <h2>Account</h2>
                    <p>Your control room identity.</p>
                  </div>
                  <div className="settings-card account-card">
                    <span className="account-badge">
                      {user.role.slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <strong>{user.username}</strong>
                      <span>{user.role} access</span>
                    </div>
                  </div>
                </section>
                {user.role !== "viewer" && (
                  <section className="settings-section">
                    <div className="section-heading">
                      <h2>Inference</h2>
                      <p>Choose runtime profile for motion-gated depth analysis.</p>
                    </div>
                    <div className="settings-card inference-card">
                      <label>
                        <span>Depth model</span>
                        <select
                          disabled={depthModels.length === 0}
                          onChange={updateDepthModel}
                          value={selectedDepthModel}
                        >
                          {depthModels.length === 0 ? (
                            <option>Loading models…</option>
                          ) : (
                            depthModels.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.label}
                              </option>
                            ))
                          )}
                        </select>
                      </label>
                      {selectedDepthModel && (
                        <small>
                          {depthModels.find((model) => model.id === selectedDepthModel)?.description}
                        </small>
                      )}
                      {modelError && <p className="model-error">{modelError}</p>}
                    </div>
                  </section>
                )}

                <section className="settings-section">
                  <div className="section-heading">
                    <h2>Preferences</h2>
                    <p>Adjust how OWL Vision behaves for you.</p>
                  </div>
                  <div className="settings-card settings-list">
                    <label className="setting-row">
                      <span>
                        <strong>Desktop alerts</strong>
                        <small>Show operator attention notifications.</small>
                      </span>
                      <Checkbox
                        checked={desktopAlerts}
                        label=""
                        onChange={(event) =>
                          setDesktopAlerts(event.target.checked)
                        }
                      />
                    </label>
                    <label className="setting-row">
                      <span>
                        <strong>Compact camera cards</strong>
                        <small>Fit more camera feeds into the grid.</small>
                      </span>
                      <Checkbox
                        checked={compactCards}
                        label=""
                        onChange={(event) =>
                          setCompactCards(event.target.checked)
                        }
                      />
                    </label>
                    <div className="setting-row">
                      <span>
                        <strong>Appearance</strong>
                        <small>Current interface palette.</small>
                      </span>
                      <span className="setting-value">Dark Graphite</span>
                    </div>
                  </div>
                </section>
              </>
            ) : section === "security" ? (
              <section className="settings-section">
                <div className="section-heading">
                  <h2>Security</h2>
                  <p>Protect access to this control room.</p>
                </div>
                <div className="settings-card settings-list">
                  <form className="password-form" onSubmit={submitPassword}>
                    <div>
                      <strong>Password</strong>
                      <small>Manage your sign-in password.</small>
                    </div>
                    <TextField
                      autoComplete="current-password"
                      label="Current password"
                      onChange={(event) =>
                        setCurrentPassword(event.target.value)
                      }
                      required
                      type="password"
                      value={currentPassword}
                    />
                    <TextField
                      autoComplete="new-password"
                      label="New password"
                      minLength={12}
                      onChange={(event) => setNewPassword(event.target.value)}
                      required
                      type="password"
                      value={newPassword}
                    />
                    <TextField
                      autoComplete="new-password"
                      label="Confirm new password"
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      required
                      type="password"
                      value={confirmPassword}
                    />
                    {passwordError && (
                      <p className="password-error">{passwordError}</p>
                    )}
                    {passwordMessage && (
                      <p className="password-success">{passwordMessage}</p>
                    )}
                    <Button disabled={savingPassword} type="submit">
                      {savingPassword ? "Saving…" : "Update password"}
                    </Button>
                  </form>
                  <div className="setting-row">
                    <span>
                      <strong>Active sessions</strong>
                      <small>Devices currently signed in.</small>
                    </span>
                    <span className="setting-value">1 active</span>
                  </div>
                </div>
              </section>
            ) : (
              <section className="settings-section">
                <div className="section-heading">
                  <h2>Activity</h2>
                  <p>Recent actions performed by your operator account.</p>
                </div>
                <div className="settings-card activity-empty">
                  <strong>No operator activity yet</strong>
                  <span>Actions you perform will appear here.</span>
                </div>
              </section>
            )}
          </Stack>
        </Grid>
      </Box>
    </Layout>
  );
}
